import wikiData from '@/data/wikiLoadoutData'
import gameIcons from '@/data/gameIconManifest.json'
import {
  SURVIVOR_ADDON_SCENARIO_EFFECTS,
  SURVIVOR_EQUIPMENT_ITEM_STATS,
  SURVIVOR_EQUIPMENT_PAIR_OVERRIDES,
  SURVIVOR_EQUIPMENT_RISKS,
  SURVIVOR_EQUIPMENT_SCENARIOS,
  SURVIVOR_ITEM_SCENARIO_BASES
} from '@/data/survivorEquipmentKnowledge'
import {
  equipmentMetricRawDirection,
  equipmentScenarioMetricAnalysis
} from '@/data/equipmentStatUtilityModel'
import { getSurvivorPerkRoleIds } from '@/data/survivorPerkRoles'
import { equipmentPlayerDoctrine } from '@/data/equipmentPlayerDoctrine'
import { evaluateEquipmentBuildStrategy } from '@/lib/equipmentBuildStrategyEngine'
import { canonicalSignals, isEventPoolEntry } from '@/lib/loadoutCorrelation'

const MEDKIT_CHARGES = {
  bandages: 8,
  gauze_roll: 10,
  gel_dressings: 16,
  self_adherent_wrap: 8
}

const FLASHLIGHT_BATTERY = {
  battery: 2,
  heavy_duty_battery: 4,
  long_life_battery: 6
}

const FLASHLIGHT_EFFECTS = {
  leather_grip: { accuracy: 20 },
  rubber_grip: { accuracy: 40 },
  wide_lens: { width: 25, reach: -25 },
  focus_lens: { reach: 25, width: -15, blindDuration: 10 },
  high_end_sapphire_lens: { reach: 25, width: -25, blindDuration: 15 },
  power_bulb: { blindDuration: 10 },
  tir_optic: { blindDuration: 15 },
  intense_halogen: { blindDuration: 20 },
  odd_bulb: { blindDuration: 25 }
}

const MEDKIT_SPEED = {
  butterfly_tape: 5,
  medical_scissors: 10,
  abdominal_dressing: 15,
  self_adherent_wrap: 5
}

const TOOLBOX_REPAIR_SPEED = {
  clean_rag: 20,
  socket_swivels: 30
}

const TOOLBOX_SABOTAGE_SPEED = {
  cutting_wire: 20,
  hacksaw: 30
}

const TOOLBOX_CHARGES = {
  scraps: 8,
  wire_spool: 12
}

const FOG_MODIFIERS = {
  mushroom_formula: { radius: 2 },
  oily_sap: { duration: 2 },
  potent_extract: { radiusMultiplier: 0.75, durationMultiplier: 0.5, opacityMultiplier: 2 },
  reactive_compound: { expansion: -1 },
  volcanic_stone: { recharge: -5 }
}

const EXECUTION_SCENARIOS = new Set([
  'flashlight_save',
  'chase_blind',
  'healing_skill_checks',
  'brand_new_part_burst',
  'sabotage_speed'
])

const PLAN_CATEGORY_PREFERENCE = {
  generator_pressure: [7],
  healing_support: [6],
  sabotage_save: [7],
  totem_control: [5],
  endgame_survival: [6, 3, 5, 1],
  stealth_evasion: [5, 8, 3, 1],
  aura_information: [5, 1],
  chase_escape: [3, 5, 8],
  rescue_teamplay: [3, 6, 7],
  slug_recovery: [6, 5]
}

const PLAN_SCENARIO_PRIORITY = {
  generator_pressure: {
    brand_new_part_burst: 42,
    generator_burst: 30,
    generator_capacity: 18,
    stealth_repair: 12,
    team_objective_mapping: 0
  },
  healing_support: {
    altruistic_heal_burst: 18,
    altruistic_heal_economy: 17,
    self_heal_safety: 12,
    double_self_heal: 12,
    healing_skill_checks: 10
  },
  sabotage_save: { sabotage_speed: 20, sabotage_denial: 18 },
  totem_control: { totem_hunting: 22 },
  stealth_evasion: {
    dense_chase_concealment: 2,
    fast_deploy_cloud: 1,
    large_lasting_cloud: 2,
    chase_blind: 7,
    blind_duration_pressure: 4
  },
  chase_escape: {
    chase_blind: 6,
    flashlight_save: 3,
    dense_chase_concealment: 2,
    fast_deploy_cloud: 0,
    route_scouting: 10,
    blind_duration_pressure: 2
  },
  aura_information: {
    team_aura_tracking: 16,
    killer_aura_tracking: 17,
    map_killer_scouting: 16,
    team_objective_mapping: 12,
    route_scouting: 10
  },
  rescue_teamplay: {
    flashlight_save: 18,
    sabotage_speed: 14,
    altruistic_heal_burst: 11
  },
  slug_recovery: {
    altruistic_heal_burst: 20,
    altruistic_heal_economy: 17,
    self_heal_safety: 13,
    dense_chase_concealment: 0,
    fast_deploy_cloud: 0,
    route_scouting: 7
  },
  item_economy: {
    generator_capacity: 14,
    flashlight_endurance: 13,
    altruistic_heal_economy: 13,
    repeatable_cloud: 12,
    chest_endgame_utility: 12
  },
  endgame_survival: { chest_endgame_utility: 20 },
  linked_teamwork: {
    team_aura_tracking: 13,
    team_objective_mapping: 13,
    altruistic_heal_economy: 12,
    flashlight_save: 10
  },
  protection_survival: {
    self_heal_safety: 14,
    double_self_heal: 13,
    dense_chase_concealment: 0,
    fast_deploy_cloud: 0
  }
}

let catalogCache = null

function normalize (value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '')
}

function unique (values) {
  return Array.from(new Set((values || []).filter(Boolean)))
}

function clamp (value, minimum, maximum) {
  return Math.max(minimum, Math.min(maximum, value))
}

function round (value, precision) {
  const factor = Math.pow(10, precision || 0)
  return Math.round(value * factor) / factor
}

function samePair (firstIds, secondIds) {
  const first = firstIds.slice().sort()
  const second = secondIds.slice().sort()
  return first[0] === second[0] && first[1] === second[1]
}

function packageTier (score) {
  if (score >= 96) return 's_plus'
  if (score >= 90) return 's'
  if (score >= 82) return 'a'
  if (score >= 72) return 'b'
  if (score >= 62) return 'c'
  return 'niche'
}

function correlationTier (score) {
  if (score >= 90) return 'perfect'
  if (score >= 80) return 'strong'
  if (score >= 68) return 'supportive'
  if (score >= 52) return 'neutral'
  return 'weak'
}

function entryAvailable (entry) {
  return Boolean(entry) && !(entry.mechanics && entry.mechanics.notAvailable) && !isEventPoolEntry(entry)
}

function pairOverride (scenarioId, addonIds) {
  return SURVIVOR_EQUIPMENT_PAIR_OVERRIDES.find(override => (
    override.scenarioId === scenarioId && samePair(override.addonIds, addonIds)
  )) || null
}

function scenarioMetrics (item, addons, scenarioId) {
  const itemStats = SURVIVOR_EQUIPMENT_ITEM_STATS[item.id] || {}
  const addonIds = addons.map(addon => addon.id)
  const metrics = []

  if (item.category === 1) {
    let charges = Number(itemStats.charges) || 0
    if (addonIds.indexOf('friendship_charm') !== -1) charges += 1
    if (addonIds.indexOf('blood_amber') !== -1) charges -= 2
    metrics.push({ id: 'uses', value: Math.max(0, charges) })
    if (scenarioId === 'killer_aura_tracking') {
      metrics.push({ id: 'killerAuraUnlocked', value: addonIds.indexOf('blood_amber') !== -1 ? 1 : 0 })
      metrics.push({ id: 'globalKillerAura', value: addonIds.indexOf('blood_amber') !== -1 ? 1 : 0 })
      const auraDuration = Math.max(0, (Number(itemStats.auraDuration) || 0) - 6 + (addonIds.indexOf('braided_bauble') !== -1 ? 2 : 0))
      metrics.push({ id: 'auraSeconds', value: auraDuration })
    } else {
      metrics.push({ id: 'auraRangeMetres', value: Number(itemStats.auraRange) || 0, suffix: 'm' })
      if (itemStats.auraDuration) {
        const duration = Number(itemStats.auraDuration) + (addonIds.indexOf('braided_bauble') !== -1 ? 2 : 0)
        metrics.push({ id: 'auraSeconds', value: duration })
      }
    }
    if (scenarioId === 'chest_endgame_utility' && itemStats.chest) {
      const interaction = 2.5 * (addonIds.indexOf('shrill_whistle') !== -1 ? 0.65 : 1)
      metrics.push({ id: 'interactionSeconds', value: round(interaction, 1) })
      metrics.push({ id: 'chestUnlocked', value: 1 })
      metrics.push({ id: 'hatchUnlocked', value: itemStats.hatch ? 1 : 0 })
    }
    if (scenarioId === 'obsession_coordination') {
      metrics.push({ id: 'permanentObsessionAura', value: addonIds.indexOf('unique_wedding_ring') !== -1 ? 1 : 0 })
    }
  }

  if (item.category === 3) {
    let battery = Number(itemStats.batterySeconds) || 8
    addonIds.forEach(addonId => { battery += FLASHLIGHT_BATTERY[addonId] || 0 })
    if (addonIds.indexOf('low_amp_filament') !== -1) battery /= 0.76
    if (itemStats.depletionReduction) battery /= (1 - itemStats.depletionReduction / 100)
    if (addonIds.indexOf('odd_bulb') !== -1) battery /= 1.14
    const effects = addonIds.reduce((total, addonId) => {
      const modifier = FLASHLIGHT_EFFECTS[addonId] || {}
      total.accuracy += modifier.accuracy || 0
      total.reach += modifier.reach || 0
      total.width += modifier.width || 0
      total.blindDuration += modifier.blindDuration || 0
      return total
    }, {
      accuracy: Number(itemStats.accuracy) || 0,
      reach: 0,
      width: 0,
      blindDuration: Number(itemStats.blindDurationBonus) || 0
    })
    const values = {
      batterySeconds: round(battery, 1),
      accuracyBonus: effects.accuracy,
      reachBonus: effects.reach,
      beamWidthBonus: effects.width,
      blindDurationBonus: effects.blindDuration,
      beamRangeMetres: round(10 * (1 + effects.reach / 100), 2),
      beamAngleDegrees: round(20 * (1 + effects.width / 100), 2),
      blindSeconds: round(2 * (1 + effects.blindDuration / 100), 2)
    }
    const order = scenarioId === 'blind_duration_pressure'
      ? ['blindSeconds', 'batterySeconds', 'beamRangeMetres', 'accuracyBonus']
      : scenarioId === 'chase_blind'
        ? ['accuracyBonus', 'beamAngleDegrees', 'beamRangeMetres', 'batterySeconds', 'blindSeconds']
        : ['batterySeconds', 'beamRangeMetres', 'accuracyBonus', 'beamAngleDegrees', 'blindSeconds']
    order.forEach(id => metrics.push({
      id,
      value: values[id],
      suffix: id === 'beamRangeMetres' ? 'm' : id === 'beamAngleDegrees' ? 'deg' : id === 'blindSeconds' || id === 'batterySeconds' ? 's' : '%'
    }))
    metrics.push({ id: 'reachBonus', value: values.reachBonus, suffix: '%' })
    metrics.push({ id: 'beamWidthBonus', value: values.beamWidthBonus, suffix: '%' })
    metrics.push({ id: 'blindDurationBonus', value: values.blindDurationBonus, suffix: '%' })
  }
  if (item.category === 5) {
    let charges = Number(itemStats.charges) || Number(String(item.mechanics && item.mechanics.charges).match(/\d+/)?.[0]) || 0
    if (addonIds.indexOf('gnarled_compass') !== -1) charges += 2
    if (addonIds.indexOf('crimson_stamp') !== -1) charges -= 2
    metrics.push({ id: 'uses', value: Math.max(0, charges) })
    const beamScenario = scenarioId === 'team_objective_mapping' || scenarioId === 'map_killer_scouting'
    const range = beamScenario
      ? 32
      : (Number(itemStats.routeRange) || 0) + (addonIds.indexOf('battered_tape') !== -1 ? 8 : 0)
    const duration = beamScenario
      ? 16 - (scenarioId === 'map_killer_scouting' && addonIds.indexOf('crimson_stamp') !== -1 ? 10 : 0)
      : (Number(itemStats.routeDuration) || 0) + (addonIds.indexOf('glowing_ink') !== -1 ? 2 : 0)
    metrics.push({ id: 'radiusMetres', value: range, suffix: 'm' })
    metrics.push({ id: 'durationSeconds', value: Math.max(0, duration), suffix: 's' })
    metrics.push({ id: 'auraRangeMetres', value: Number(itemStats.routeRange) || 0, suffix: 'm' })
    if (scenarioId === 'totem_hunting') {
      metrics.push({ id: 'totemTracking', value: addonIds.indexOf('sharpened_flint') !== -1 ? 1 : 0 })
    }
    if (scenarioId === 'map_killer_scouting' && addonIds.indexOf('crimson_stamp') !== -1) {
      metrics.push({ id: 'killerDetectionRadius', value: 8 })
    }
  }
  if (item.category === 6) {
    let charges = Number(itemStats.charges) || 24
    addonIds.forEach(addonId => { charges += MEDKIT_CHARGES[addonId] || 0 })
    metrics.push({ id: 'charges', value: charges })
    const speedBonus = addonIds.reduce((sum, addonId) => sum + (MEDKIT_SPEED[addonId] || 0), 0)
    const selfHealEfficiency = addonIds.indexOf('styptic_agent') !== -1 ? 15 : 0
    const selfHealChargeCost = 16 / ((1 + selfHealEfficiency / 100) * (2 / 3))
    if (scenarioId === 'double_self_heal' || scenarioId === 'self_heal_safety') {
      metrics.push({ id: 'completeSelfHeals', value: Math.floor(charges / selfHealChargeCost) })
      metrics.push({ id: 'selfHealChargeCost', value: round(selfHealChargeCost, 2) })
      metrics.push({ id: 'healSeconds', value: round(16 / ((2 / 3) * (1 + speedBonus / 100)), 1) })
    }
    if (scenarioId.indexOf('altruistic') === 0) {
      const totalSpeed = (Number(itemStats.altruisticSpeed) || 0) + speedBonus
      metrics.push({ id: 'completeAltruisticHeals', value: Math.floor(charges / 16) })
      metrics.push({ id: 'altruisticSpeedBonus', value: totalSpeed, suffix: '%' })
      metrics.push({ id: 'healSeconds', value: round(16 / (1 + totalSpeed / 100), 1) })
    }
    if (scenarioId === 'healing_skill_checks') {
      metrics.push({
        id: 'skillCheckChanceBonus',
        value: (addonIds.indexOf('needle_thread') !== -1 ? 10 : 0) + (addonIds.indexOf('surgical_suture') !== -1 ? 15 : 0),
        suffix: '%'
      })
      metrics.push({
        id: 'greatZoneBonus',
        value: (addonIds.indexOf('rubber_gloves') !== -1 ? 10 : 0) + (addonIds.indexOf('sponge') !== -1 ? 20 : 0),
        suffix: '%'
      })
      metrics.push({
        id: 'greatProgressBonus',
        value: (addonIds.indexOf('needle_thread') !== -1 ? 5 : 0) + (addonIds.indexOf('surgical_suture') !== -1 ? 10 : 0),
        suffix: '%'
      })
    }
    if (scenarioId === 'exhaustion_reset') {
      metrics.push({ id: 'exhaustionReset', value: addonIds.indexOf('anti_exhaustion_syringe') !== -1 ? 1 : 0 })
      metrics.push({ id: 'healSeconds', value: round(16 / ((2 / 3) * (1 + speedBonus / 100)), 1) })
    }
  }
  if (item.category === 7) {
    let charges = Number(itemStats.charges) || 0
    addonIds.forEach(addonId => { charges += TOOLBOX_CHARGES[addonId] || 0 })
    metrics.push({ id: 'charges', value: charges })
    if (scenarioId.indexOf('generator') !== -1 || scenarioId === 'brand_new_part_burst' || scenarioId === 'stealth_repair') {
      const requirementReduction = addonIds.indexOf('brand_new_part') !== -1 ? 10 : 0
      metrics.push({ id: 'generatorProgress', value: round(charges / 90 * 100, 1), suffix: '%' })
      metrics.push({ id: 'generatorObjectiveProgress', value: round((charges + requirementReduction) / 90 * 100, 1), suffix: '%' })
      if (requirementReduction) metrics.push({ id: 'generatorRequirementReduction', value: requirementReduction })
      if (addonIds.indexOf('spring_clamp') !== -1) metrics.push({ id: 'noiseRadiusReduction', value: 8 })
      const repairSpeed = (Number(itemStats.repairSpeed) || 0) +
        addonIds.reduce((sum, addonId) => sum + (TOOLBOX_REPAIR_SPEED[addonId] || 0), 0)
      const deliverySeconds = charges / Math.max(0.1, 1 + repairSpeed / 100)
      const deliveredInTenSeconds = Math.min(charges, 10 * (1 + repairSpeed / 100))
      metrics.push({ id: 'repairSpeedBonus', value: repairSpeed, suffix: '%' })
      metrics.push({ id: 'deliverySeconds', value: round(deliverySeconds, 1) })
      metrics.push({ id: 'generatorTimeSaved', value: round(Math.max(0, charges - deliverySeconds), 1) })
      metrics.push({ id: 'generatorProgressIn10Seconds', value: round(deliveredInTenSeconds / 90 * 100, 1), suffix: '%' })
    }
    if (scenarioId.indexOf('sabotage') !== -1) {
      const sabotageSpeed = (Number(itemStats.sabotageSpeed) || 0) +
        addonIds.reduce((sum, addonId) => sum + (TOOLBOX_SABOTAGE_SPEED[addonId] || 0), 0)
      const sabotageSeconds = 3 / Math.max(0.1, 1 + sabotageSpeed / 100)
      metrics.push({ id: 'sabotageSpeedBonus', value: sabotageSpeed, suffix: '%' })
      metrics.push({ id: 'sabotageSeconds', value: round(sabotageSeconds, 2) })
      metrics.push({ id: 'sabotageActions', value: Math.floor(charges / 6) })
      if (addonIds.indexOf('grip_wrench') !== -1) metrics.push({ id: 'extraHookRespawnSeconds', value: 20 })
      if (addonIds.indexOf('protective_gloves') !== -1) metrics.push({ id: 'notificationSuppressed', value: 1 })
    }
  }
  if (item.category === 8) {
    const stats = {
      radius: Number(itemStats.radius) || 8,
      expansion: Number(itemStats.expansion) || 2,
      duration: Number(itemStats.duration) || 8,
      recharge: Number(itemStats.recharge) || 70
    }
    addonIds.forEach(addonId => {
      const modifier = FOG_MODIFIERS[addonId] || {}
      stats.radius += modifier.radius || 0
      stats.expansion += modifier.expansion || 0
      stats.duration += modifier.duration || 0
      stats.recharge += modifier.recharge || 0
      stats.radius *= modifier.radiusMultiplier || 1
      stats.duration *= modifier.durationMultiplier || 1
    })
    metrics.push({ id: 'radiusMetres', value: round(stats.radius, 1) })
    metrics.push({ id: 'durationSeconds', value: round(stats.duration, 1) })
    metrics.push({ id: 'deploySeconds', value: round(Math.max(0.2, stats.expansion), 1) })
    metrics.push({ id: 'rechargeSeconds', value: round(Math.max(0, stats.recharge), 1) })
    metrics.push({ id: 'opacityPercent', value: addonIds.indexOf('potent_extract') !== -1 ? 66 : 33, suffix: '%' })
    metrics.push({ id: 'uses', value: Number(itemStats.charges) || 4 })
  }

  return metrics
}

function scenarioMetricImpact (scenarioId, metrics) {
  return equipmentScenarioMetricAnalysis(scenarioId, metrics)
}

function scenarioStrength (item, addons, scenario) {
  const addonIds = addons.map(addon => addon.id)
  const override = pairOverride(scenario.id, addonIds)
  const metrics = scenarioMetrics(item, addons, scenario.id)
  const playerDoctrine = equipmentPlayerDoctrine(scenario.id)
  const metricAnalysis = scenarioMetricImpact(scenario.id, metrics)
  const raw = metricAnalysis ? metricAnalysis.score : 35
  const evidence = [
    { id: 'mechanicsDerivedScore', value: round(raw, 1), detail: scenario.id }
  ]

  if (metricAnalysis) {
    if (metricAnalysis.requirementPenalty > 0) {
      evidence.push({
        id: 'requirementPenalty',
        value: -metricAnalysis.requirementPenalty,
        detail: metricAnalysis.criticalGaps.join(',') || 'importantThreshold'
      })
    }
  }

  const score = clamp(round(raw, 1), 20, 99)
  return {
    scenario,
    playerDoctrine,
    score,
    rawScore: round(raw, 1),
    tier: packageTier(score),
    metrics,
    statInsights: metricAnalysis ? metricAnalysis.insights : [],
    priorityUtility: metricAnalysis ? metricAnalysis.priorityUtility : 0,
    requirementsPassed: metricAnalysis ? metricAnalysis.requirementsPassed : true,
    criticalGaps: metricAnalysis ? metricAnalysis.criticalGaps : [],
    requirementPenalty: metricAnalysis ? metricAnalysis.requirementPenalty : 0,
    evidence: evidence.filter(entry => entry.value),
    curated: Boolean(override)
  }
}

function packageRisks (addons) {
  return unique(addons.reduce((risks, addon) => risks.concat(SURVIVOR_EQUIPMENT_RISKS[addon.id] || []), []))
}

function compilePackage (item, addons) {
  const addonIds = addons.map(addon => addon.id).sort()
  const scenarioScores = SURVIVOR_EQUIPMENT_SCENARIOS
    .filter(scenario => scenario.category === item.category)
    .map(scenario => scenarioStrength(item, addons, scenario))
    .sort((first, second) => second.score - first.score || first.scenario.id.localeCompare(second.scenario.id))
  const dominant = scenarioScores[0]
  const risks = packageRisks(addons)
  const curatedCount = scenarioScores.filter(result => result.curated).length
  const competitiveScenarios = scenarioScores.filter(result => result.score >= 78).length
  const versatility = clamp(Math.round(42 + competitiveScenarios * 12 + Math.min(12, scenarioScores.slice(0, 3).reduce((sum, result) => sum + result.score, 0) / 25)), 35, 96)
  const consistency = clamp(82 - risks.length * 4 - (EXECUTION_SCENARIOS.has(dominant.scenario.id) ? 5 : 0), 52, 96)
  const knowledgeCoverage = addons.filter(addon => SURVIVOR_ADDON_SCENARIO_EFFECTS[addon.id]).length
  const confidence = clamp(68 + (SURVIVOR_ITEM_SCENARIO_BASES[item.id] ? 8 : 0) + knowledgeCoverage * 7 + (curatedCount ? 7 : 0), 50, 97)

  return {
    id: `${item.id}__${addonIds.join('__')}`,
    category: item.category,
    item,
    addons: addonIds.map(addonId => addons.find(addon => addon.id === addonId)),
    addonIds,
    scenarioScores,
    dominantScenario: dominant.scenario,
    peakStrength: dominant.score,
    peakTier: dominant.tier,
    versatility,
    consistency,
    confidence,
    risks,
    curated: curatedCount > 0,
    rouletteReady: Boolean(
      gameIcons.items[String(item.id)] &&
      addons.every(addon => gameIcons.addons[String(addon.id)])
    )
  }
}

function activeItems () {
  return wikiData.items.filter(entryAvailable).filter(item => SURVIVOR_ITEM_SCENARIO_BASES[item.id])
}

function activeAddons () {
  return wikiData.survivorAddons.filter(entryAvailable).filter(addon => SURVIVOR_ADDON_SCENARIO_EFFECTS[addon.id])
}

function scenarioOutcomeDominance (first, second) {
  const firstInsights = (first.statInsights || []).reduce((insights, insight) => {
    insights[insight.metricId] = insight
    return insights
  }, {})
  const secondInsights = second.statInsights || []
  if (!secondInsights.length) return first.rawScore >= second.rawScore ? Number(first.rawScore > second.rawScore) : -1
  let utilityStrict = false
  for (let index = 0; index < secondInsights.length; index += 1) {
    const secondInsight = secondInsights[index]
    const firstInsight = firstInsights[secondInsight.metricId]
    if (!firstInsight || firstInsight.utility < secondInsight.utility) return -1
    if (firstInsight.utility > secondInsight.utility) utilityStrict = true
  }
  if (utilityStrict) return 1
  let rawStrict = false
  for (let index = 0; index < secondInsights.length; index += 1) {
    const secondInsight = secondInsights[index]
    const firstInsight = firstInsights[secondInsight.metricId]
    const direction = equipmentMetricRawDirection(second.scenario.id, secondInsight.metricId)
    if (!direction) continue
    const difference = direction * (Number(firstInsight.value) - Number(secondInsight.value))
    if (difference < -0.001) return -1
    if (difference > 0.001) rawStrict = true
  }
  return rawStrict ? 1 : 0
}

function packageDominates (first, second) {
  if (!first || !second || first.category !== second.category || first.id === second.id) return false
  if (first.consistency < second.consistency) return false
  const firstResults = first.scenarioScores.reduce((results, result) => {
    results[result.scenario.id] = result
    return results
  }, {})
  let strictlyBetter = first.consistency > second.consistency
  const neverWorse = second.scenarioScores.every(result => {
    const firstResult = firstResults[result.scenario.id]
    if (!firstResult) return false
    const dominance = scenarioOutcomeDominance(firstResult, result)
    if (dominance < 0) return false
    if (dominance > 0) strictlyBetter = true
    return true
  })
  return neverWorse && strictlyBetter
}

function annotateParetoFront (packages) {
  const categories = packages.reduce((groups, equipmentPackage) => {
    const group = groups[equipmentPackage.category] || []
    group.push(equipmentPackage)
    groups[equipmentPackage.category] = group
    return groups
  }, {})
  return packages.map(equipmentPackage => {
    const dominator = categories[equipmentPackage.category].find(candidate => packageDominates(candidate, equipmentPackage))
    return Object.assign({}, equipmentPackage, {
      paretoOptimal: !dominator,
      dominatedBy: dominator ? dominator.id : null
    })
  })
}

export function paretoFront (packages) {
  return packages.filter(equipmentPackage => (
    !packages.some(candidate => packageDominates(candidate, equipmentPackage))
  ))
}

export function createEquipmentPackageCatalog () {
  if (catalogCache) return catalogCache
  const addons = activeAddons()
  const packages = activeItems().reduce((packages, item) => {
    const compatible = addons.filter(addon => addon.itemsCategory === item.category)
    for (let first = 0; first < compatible.length; first += 1) {
      for (let second = first + 1; second < compatible.length; second += 1) {
        packages.push(compilePackage(item, [compatible[first], compatible[second]]))
      }
    }
    return packages
  }, [])
  catalogCache = annotateParetoFront(packages)
  return catalogCache
}

function perkScenarioEvidence (scenario, perks) {
  const signalSet = new Set(scenario.signals || [])
  const keywordSet = scenario.keywords || []
  const details = (perks || []).map(perk => {
    const source = [perk.id, perk.name]
      .concat(perk.tags || [], perk.roles || [], perk.primaryRole || [])
      .concat(getSurvivorPerkRoleIds(perk.id))
    const normalized = source.map(normalize)
    const signals = canonicalSignals(source)
    const signalMatches = signals.filter(signal => signalSet.has(signal))
    const keywordMatches = keywordSet.filter(keyword => normalized.some(value => value.indexOf(normalize(keyword)) !== -1))
    return {
      perkId: perk.id,
      matched: signalMatches.length > 0 || keywordMatches.length > 0,
      signalMatches,
      keywordMatches
    }
  })
  return {
    coverage: details.filter(detail => detail.matched).length,
    satelliteCoverage: details.slice(1).filter(detail => detail.matched).length,
    central: details[0] || { perkId: '', matched: false, signalMatches: [], keywordMatches: [] },
    signals: unique(details.reduce((all, detail) => all.concat(detail.signalMatches), [])),
    keywords: unique(details.reduce((all, detail) => all.concat(detail.keywordMatches), [])),
    details
  }
}

function scenarioBuildFit (scenario, perks, plan) {
  const evidence = perkScenarioEvidence(scenario, perks)
  const primaryPlan = plan && (plan.primary || plan.id)
  const secondaryPlan = plan && plan.secondary
  const primaryMatch = scenario.plans.indexOf(primaryPlan) !== -1
  const secondaryMatch = scenario.plans.indexOf(secondaryPlan) !== -1
  const primarySignalMatch = scenario.signals.some(signal => (
    plan && (plan.primarySignals || []).indexOf(signal) !== -1
  ))
  const secondarySignalMatch = scenario.signals.some(signal => (
    plan && (plan.secondarySignals || []).indexOf(signal) !== -1
  ))
  const priorityBonus = (PLAN_SCENARIO_PRIORITY[primaryPlan] && PLAN_SCENARIO_PRIORITY[primaryPlan][scenario.id]) || 0
  const centralSignalCount = evidence.central.signalMatches.length
  const centralKeywordCount = evidence.central.keywordMatches.length
  const centralFit = clamp(
    18 +
    (evidence.central.matched ? 48 : 0) +
    Math.min(16, centralSignalCount * 6 + centralKeywordCount * 3) +
    (primaryMatch ? 7 : 0) +
    (primarySignalMatch ? 5 : 0) +
    Math.min(5, priorityBonus * 0.2),
    18,
    99
  )
  const score = clamp(
    18 +
    evidence.coverage * 8 +
    Math.min(10, evidence.signals.length * 4 + evidence.keywords.length * 2) +
    (primaryMatch ? 20 : 0) +
    (secondaryMatch ? 10 : 0) +
    (primarySignalMatch ? 8 : 0) +
    (secondarySignalMatch ? 4 : 0) +
    priorityBonus,
    20,
    99
  )
  return {
    score,
    centralFit,
    centralMatched: evidence.central.matched,
    centralPerkId: evidence.central.perkId,
    satelliteCoverage: evidence.satelliteCoverage,
    coverage: evidence.coverage,
    coverageTotal: (perks || []).length,
    signals: evidence.signals,
    keywords: evidence.keywords,
    primaryMatch,
    secondaryMatch,
    priorityBonus
  }
}

function evaluatePackageForBuild (equipmentPackage, perks, plan, forcedScenarioId) {
  const matches = equipmentPackage.scenarioScores
    .filter(result => forcedScenarioId
      ? result.scenario.id === forcedScenarioId
      : true)
    .map(result => {
      const semanticBuildFit = scenarioBuildFit(result.scenario, perks, plan)
      const strategyAnalysis = evaluateEquipmentBuildStrategy(equipmentPackage, result, perks, plan)
      const strategyCoverage = strategyAnalysis.primary ? strategyAnalysis.primary.perkCoverage || 0 : 0
      const centralStrategy = (strategyAnalysis.candidates || []).find(candidate => candidate.centralPerkFit)
      const buildFit = Object.assign({}, semanticBuildFit, {
        centralFit: Math.max(semanticBuildFit.centralFit, centralStrategy ? 99 : 0),
        centralMatched: semanticBuildFit.centralMatched || Boolean(centralStrategy),
        centralStrategyId: centralStrategy ? centralStrategy.id : '',
        centralPerkId: semanticBuildFit.centralPerkId || (centralStrategy && centralStrategy.centralPerkId) || '',
        semanticCoverage: semanticBuildFit.coverage,
        coverage: Math.max(semanticBuildFit.coverage, strategyCoverage)
      })
      const total = round(
        result.score * 0.63 +
        buildFit.centralFit * 0.23 +
        buildFit.score * 0.14 +
        strategyAnalysis.adjustment,
        3
      )
      return Object.assign({}, result, {
        buildFit,
        total,
        buildStrategy: strategyAnalysis.primary,
        strategyAnalysis
      })
    }).filter(result => (
      Boolean(forcedScenarioId) || result.scenario.automatic !== false || Boolean(result.buildStrategy)
    )).sort((first, second) => (
      second.total - first.total ||
      second.buildFit.centralFit - first.buildFit.centralFit ||
      second.buildFit.coverage - first.buildFit.coverage ||
      second.score - first.score
    ))
  const best = matches[0]
  if (!best) return null
  return Object.assign({}, equipmentPackage, {
    matchingScenario: best.scenario,
    matchingStrength: best.score,
    matchingTier: best.tier,
    buildFit: best.buildFit,
    buildScore: clamp(Math.round(best.total), 1, 99),
    rawBuildScore: best.total,
    matchingMetrics: best.metrics.concat(best.strategyAnalysis.metrics),
    matchingStatInsights: best.statInsights,
    matchingPlayerDoctrine: best.playerDoctrine,
    matchingEvidence: best.evidence.concat(best.strategyAnalysis.evidence),
    buildStrategy: best.buildStrategy,
    strategyAdjustment: best.strategyAnalysis.adjustment,
    strategyCandidates: best.strategyAnalysis.candidates,
    evaluatedScenarios: matches
  })
}

function couldGainBuildStrategy (equipmentPackage, perks, plan) {
  const perkIds = new Set((perks || []).map(perk => perk.id))
  const addonIds = equipmentPackage.addonIds || []
  const hasPair = (first, second) => addonIds.indexOf(first) !== -1 && addonIds.indexOf(second) !== -1
  const primaryPlan = plan && (plan.primary || plan.id)
  const centralPerkId = perks && perks[0] ? perks[0].id : ''
  const pickupDenialCount = (perks || []).filter(perk => (
    getSurvivorPerkRoleIds(perk.id).indexOf('pickup_denial') !== -1
  )).length

  if (equipmentPackage.category === 1) {
    const keyChestPerks = ['ace_in_the_hole', 'appraisal', 'moment_of_glory', 'pharmacy', 'plunderers_instinct', 'specialist']
    const keyHatchPerks = ['left_behind', 'low_profile', 'sole_survivor']
    const keyTeamLinks = ['blood_pact', 'teamwork_collective_stealth', 'teamwork_full_circuit', 'teamwork_power_of_two', 'teamwork_soft_spoken', 'teamwork_throw_down', 'teamwork_toughen_up']
    const keyTeamPayoffs = ['better_than_new', 'corrective_action', 'friendly_competition', 'leader', 'prove_thyself', 'streetwise', 'vigil']
    const keyChestCount = keyChestPerks.filter(perkId => perkIds.has(perkId)).length
    const keyHatchCount = keyHatchPerks.filter(perkId => perkIds.has(perkId)).length
    const keyTeamLinkCount = keyTeamLinks.filter(perkId => perkIds.has(perkId)).length
    const keyTeamPayoffCount = keyTeamPayoffs.filter(perkId => perkIds.has(perkId)).length
    const skeleton = equipmentPackage.item.id === 'skeleton_key'
    if (skeleton && centralPerkId === 'eyes_of_belmont' && hasPair('blood_amber', 'braided_bauble')) return true
    if (skeleton && keyChestPerks.indexOf(centralPerkId) !== -1 && keyChestCount >= 2 && hasPair('friendship_charm', 'shrill_whistle')) return true
    if ((skeleton || equipmentPackage.item.id === 'dull_key') && keyHatchPerks.indexOf(centralPerkId) !== -1 && keyHatchCount >= 2 && hasPair('friendship_charm', 'shrill_whistle')) return true
    if (skeleton && keyTeamLinks.indexOf(centralPerkId) !== -1 && keyTeamLinkCount >= 1 && keyTeamLinkCount + keyTeamPayoffCount >= 2 && hasPair('braided_bauble', 'friendship_charm')) return true
    if (skeleton && centralPerkId === 'blood_pact' && keyTeamLinkCount + keyTeamPayoffCount >= 2 && hasPair('friendship_charm', 'unique_wedding_ring')) return true
  }

  if (equipmentPackage.category === 7) {
    const rechargeableCapacityLoop = perkIds.has('built_to_last') || perkIds.has('scavenger')
    const residualPipelinePair = rechargeableCapacityLoop
      ? hasPair('wire_spool', 'scraps')
      : hasPair('brand_new_part', 'wire_spool')
    if (rechargeableCapacityLoop && hasPair('wire_spool', 'scraps')) return true
    if (perkIds.has('residual_manifest') && perkIds.has('flashbang') &&
      equipmentPackage.item.id === 'commodious_toolbox' && residualPipelinePair) return true
    if (primaryPlan === 'generator_pressure' && addonIds.indexOf('brand_new_part') !== -1 &&
      !perkIds.has('built_to_last') && !perkIds.has('scavenger')) return true
  }

  if (equipmentPackage.category === 3) {
    if (perkIds.has('champion_of_light') &&
      (hasPair('high_end_sapphire_lens', 'long_life_battery') ||
        hasPair('high_end_sapphire_lens', 'heavy_duty_battery') ||
        hasPair('high_end_sapphire_lens', 'intense_halogen') ||
        hasPair('odd_bulb', 'intense_halogen') || hasPair('odd_bulb', 'high_end_sapphire_lens'))) return true
    if ((perkIds.has('background_player') || pickupDenialCount >= 2) &&
      (hasPair('wide_lens', 'long_life_battery') ||
        hasPair('wide_lens', 'rubber_grip') ||
        hasPair('wide_lens', 'low_amp_filament'))) return true
    if (perkIds.has('built_to_last') && hasPair('long_life_battery', 'low_amp_filament')) return true
  }

  const fogPrerunPerks = (perks || []).filter(perk => {
    const roles = getSurvivorPerkRoleIds(perk.id)
    return roles.some(role => [
      'scratch_mark_denial', 'line_of_sight_break', 'post_vault_disappearance'
    ].indexOf(role) !== -1)
  }).length
  if (equipmentPackage.category === 8 && fogPrerunPerks >= 2 &&
    equipmentPackage.item.id === 'vigo_s_fog_vial' && hasPair('mushroom_formula', 'oily_sap')) return true

  const antiSlugPerks = ['were_gonna_live_forever', 'buckle_up', 'for_the_people']
    .filter(perkId => perkIds.has(perkId)).length
  if (equipmentPackage.category === 6 && antiSlugPerks >= 2 && equipmentPackage.item.id === 'ranger_med_kit') {
    return hasPair('gel_dressings', 'self_adherent_wrap') ||
      hasPair('gel_dressings', 'gauze_roll') ||
      hasPair('abdominal_dressing', 'medical_scissors')
  }
  return false
}

export function rankEquipmentPackages (options) {
  const settings = options || {}
  const lockedAddonIds = (settings.lockedAddonIds || []).filter(Boolean)
  const excludedAddonIds = new Set((settings.excludedAddonIds || []).filter(Boolean))
  const noMither = (settings.perks || []).some(perk => perk.id === 'no_mither')
  const strictOptimization = Number(settings.chaos || 0) === 0 && settings.allowDominated !== true
  const sourcePackages = strictOptimization
    ? createEquipmentPackageCatalog().filter(equipmentPackage => (
      equipmentPackage.paretoOptimal ||
      couldGainBuildStrategy(equipmentPackage, settings.perks || [], settings.plan || null)
    ))
    : createEquipmentPackageCatalog()
  const primaryPlan = settings.plan && (settings.plan.primary || settings.plan.id)
  const preferredCategories = strictOptimization && !settings.itemId && !settings.category && !settings.scenarioId
    ? PLAN_CATEGORY_PREFERENCE[primaryPlan]
    : null
  const planPackages = preferredCategories
    ? sourcePackages.filter(equipmentPackage => (
      preferredCategories.indexOf(equipmentPackage.category) !== -1 ||
      (equipmentPackage.category === 1 && couldGainBuildStrategy(equipmentPackage, settings.perks || [], settings.plan || null))
    ))
    : sourcePackages
  const filteredPackages = (planPackages.length ? planPackages : sourcePackages).filter(equipmentPackage => {
    if (settings.rouletteReady !== false && !equipmentPackage.rouletteReady) return false
    if (noMither && equipmentPackage.category === 6) return false
    if (settings.itemId && equipmentPackage.item.id !== settings.itemId) return false
    if (settings.category && equipmentPackage.category !== settings.category) return false
    if (settings.scenarioId && !equipmentPackage.scenarioScores.some(result => result.scenario.id === settings.scenarioId)) return false
    if (settings.excludedItemIds && settings.excludedItemIds.indexOf(equipmentPackage.item.id) !== -1) return false
    if (settings.excludedPackageIds && settings.excludedPackageIds.indexOf(equipmentPackage.id) !== -1) return false
    if (lockedAddonIds.some(addonId => equipmentPackage.addonIds.indexOf(addonId) === -1)) return false
    if (equipmentPackage.addonIds.some(addonId => excludedAddonIds.has(addonId) && lockedAddonIds.indexOf(addonId) === -1)) return false
    return true
  })
  const evaluatedPackages = filteredPackages.map(equipmentPackage => evaluatePackageForBuild(
    equipmentPackage,
    settings.perks || [],
    settings.plan || null,
    settings.scenarioId || null
  )).filter(Boolean)
  const optimizedPackages = strictOptimization
    ? evaluatedPackages.filter(equipmentPackage => equipmentPackage.paretoOptimal || equipmentPackage.buildStrategy)
    : evaluatedPackages
  return (optimizedPackages.length ? optimizedPackages : evaluatedPackages)
    .sort((first, second) => (
      second.rawBuildScore - first.rawBuildScore ||
      second.buildFit.centralFit - first.buildFit.centralFit ||
      second.buildFit.coverage - first.buildFit.coverage ||
      second.matchingStrength - first.matchingStrength ||
      first.id.localeCompare(second.id)
    ))
}

function weightedChoice (entries, chaos, random) {
  if (!entries.length) return null
  const normalizedChaos = clamp(Number(chaos) || 0, 0, 100) / 100
  if (normalizedChaos === 0) return entries[0]
  if (normalizedChaos >= 0.96) return entries[Math.floor(random() * entries.length)]
  const exponent = 1.2 + (1 - normalizedChaos) * 3
  const weights = entries.map(entry => Math.pow(Math.max(1, entry.rawBuildScore), exponent))
  const total = weights.reduce((sum, weight) => sum + weight, 0)
  let cursor = random() * total
  for (let index = 0; index < entries.length; index += 1) {
    cursor -= weights[index]
    if (cursor <= 0) return entries[index]
  }
  return entries[entries.length - 1]
}

export function selectEquipmentPackage (options) {
  const settings = options || {}
  const random = settings.random || Math.random
  const ranked = rankEquipmentPackages(settings)
  return weightedChoice(ranked, settings.chaos, random)
}

export function equipmentPackageCorrelation (equipmentPackage) {
  if (!equipmentPackage) return null
  const score = clamp(Math.round(equipmentPackage.buildScore || equipmentPackage.peakStrength), 1, 99)
  const scenario = equipmentPackage.matchingScenario || equipmentPackage.dominantScenario
  const buildFit = equipmentPackage.buildFit || { coverage: 0, coverageTotal: 0, signals: [] }
  const strategy = equipmentPackage.buildStrategy || null
  return {
    score,
    rawScore: score,
    tier: correlationTier(score),
    signals: buildFit.signals || [],
    reasons: [strategy ? `Advanced strategy: ${strategy.id}` : `Equipment scenario: ${scenario.id}`],
    breakdown: [
      { id: 'scenarioStrength', value: equipmentPackage.matchingStrength || equipmentPackage.peakStrength, detail: scenario.id },
      { id: 'centralPerkFit', value: buildFit.centralFit || 0, detail: buildFit.centralPerkId || '' },
      { id: 'wholeBuildFit', value: buildFit.score || 0, detail: `${buildFit.coverage || 0}/${buildFit.coverageTotal || 0}` },
      { id: 'advancedStrategy', value: equipmentPackage.strategyAdjustment || 0, detail: strategy ? strategy.id : '' },
      { id: 'packageConsistency', value: equipmentPackage.consistency, detail: '' }
    ],
    pairPlan: scenario.id,
    scenarioId: scenario.id,
    scenario,
    scenarioStrength: equipmentPackage.matchingStrength || equipmentPackage.peakStrength,
    scenarioTier: equipmentPackage.matchingTier || equipmentPackage.peakTier,
    packageId: equipmentPackage.id,
    coverage: buildFit.coverage || 0,
    coverageTotal: buildFit.coverageTotal || 0,
    confidence: equipmentPackage.confidence,
    risks: equipmentPackage.risks,
    metrics: equipmentPackage.matchingMetrics || [],
    statInsights: equipmentPackage.matchingStatInsights || [],
    playerDoctrine: equipmentPlayerDoctrine(scenario.id),
    strategy
  }
}

export function equipmentPackageMetrics () {
  const catalog = createEquipmentPackageCatalog()
  const categories = catalog.reduce((counts, equipmentPackage) => {
    counts[equipmentPackage.category] = (counts[equipmentPackage.category] || 0) + 1
    return counts
  }, {})
  return {
    packages: catalog.length,
    rouletteReady: catalog.filter(equipmentPackage => equipmentPackage.rouletteReady).length,
    scenarios: SURVIVOR_EQUIPMENT_SCENARIOS.length,
    curatedPackages: catalog.filter(equipmentPackage => equipmentPackage.curated).length,
    paretoPackages: catalog.filter(equipmentPackage => equipmentPackage.paretoOptimal).length,
    dominatedPackages: catalog.filter(equipmentPackage => !equipmentPackage.paretoOptimal).length,
    sTierPackages: catalog.filter(equipmentPackage => equipmentPackage.peakStrength >= 90).length,
    categories
  }
}

export function equipmentScenarioLabel (scenario, locale) {
  if (!scenario) return ''
  const language = String(locale || 'en').split('-')[0]
  return scenario.labels[language] || scenario.labels.en
}

export function equipmentScenarioText (scenario, field, locale) {
  if (!scenario || !scenario[field]) return ''
  const language = String(locale || 'en').split('-')[0]
  return scenario[field][language] || scenario[field].en
}

export function equipmentStrategyText (strategy, field, locale) {
  if (!strategy) return ''
  const definition = strategy.definition || strategy
  if (!definition || !definition[field]) return ''
  const language = String(locale || 'en').split('-')[0]
  return definition[field][language] || definition[field].en || ''
}
